import { PERMANENT_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { HTTPStatuses, type HttpError } from '@packages/domain/constants/http';
import {
  isHttpErrorWithParsedBody,
  isHttpErrorWithStatus,
} from '@packages/domain/utils/errors/errors';
import { saveAcademicFields } from 'actions/entities/academic/academic';
import { entitiesPayload } from 'actions/entities/orders/orders';
import { enableFeature } from 'actions/features/features';
import {
  trackSubmitPayerIdentity,
  trackSubscriptionWhatsapp,
} from 'actions/tracker/tracker';
import { fetchDocuments } from 'actions/trackingPage/trackingPage';
import { newNotification, setDocCollectorFinished } from 'actions/ui/ui';
import { SAVE_ACADEMIC_FIELDS_FAILURE } from 'constants/academic';
import { CAPTCHA_VALIDATION_RESPONSE_TOKEN } from 'constants/captcha';
import { PAYMENT_FETCH_ERROR } from 'constants/errors';
import { MAINTENANCE_MODE } from 'constants/features';
import {
  CHINESE_FIELD_ID,
  PAYER_IDENTITY_FIELD,
  PAYER_PASSPORT_FIELD_ID,
  PROMOTIONS_OPT_IN_FIELD,
  STUDENT_CHINESE_FIELD_ID,
  STUDENT_CNY_ID_NUMBER,
  STUDENT_PASSPORT_FIELD_ID,
} from 'constants/fields';
import { CREATE_ORDER_SUCCESS } from 'constants/orders';
import {
  CANCEL_PAYMENT_FAILURE,
  CANCEL_PAYMENT_REQUEST,
  CANCEL_PAYMENT_SUCCESS,
  CREATE_PAYMENT_FAILURE,
  CREATE_PAYMENT_REQUEST,
  CREATE_PAYMENT_SUCCESS,
  DELETE_PAYMENT_LRS_FAILURE,
  DELETE_PAYMENT_LRS_REQUEST,
  DELETE_PAYMENT_LRS_SUCCESS,
  FETCH_DOCUMENTS_COLLECTIONS_FAILURE,
  FETCH_DOCUMENTS_COLLECTIONS_REQUEST,
  FETCH_DOCUMENTS_COLLECTIONS_SUCCESS,
  FETCH_DOCUMENTS_COLLECTIONS_SUCCESS_WITHOUT_RESULTS,
  FETCH_LOGGED_USER_PAYMENT_FAILURE,
  FETCH_LOGGED_USER_PAYMENT_REQUEST,
  FETCH_LOGGED_USER_PAYMENT_SUCCESS,
  FETCH_PAYMENT_FAILURE,
  FETCH_PAYMENT_REQUEST,
  FETCH_PAYMENT_SUCCESS,
  FETCH_SMS_NOTIFICATION_FAILURE,
  FETCH_SMS_NOTIFICATION_REQUEST,
  FETCH_SMS_NOTIFICATION_SUCCESS,
  FETCH_USER_PAYMENT_FAILURE,
  FETCH_USER_PAYMENT_REQUEST,
  FETCH_USER_PAYMENT_SUCCESS,
  FETCH_USER_PAYMENTS_FAILURE,
  FETCH_USER_PAYMENTS_REQUEST,
  FETCH_USER_PAYMENTS_SUCCESS,
  INITIATE_PAYMENT_REQUEST,
  INITIATE_PAYMENT_SUCCESS,
  PAYMENT_PROOF_COMPLETED,
  SAVE_LRS_FORM_DATA_REQUEST,
  SAVE_LRS_FORM_DATA_SUCCESS,
  UPDATE_PAYMENT_FAILURE,
  UPDATE_PAYMENT_REQUEST,
  UPDATE_PAYMENT_SUCCESS,
  UPLOAD_LRS_FAILURE,
  UPLOAD_LRS_REQUEST,
  UPLOAD_LRS_SIGNATURE_FILE_BAD_FORMAT,
  UPLOAD_LRS_SUCCESS,
} from 'constants/payment';
import { UPDATE_SENDER_EMAIL } from 'constants/sender';
import type { ApiError } from 'models/error';
import type { FieldEntityType } from 'models/field';
import type { PreviousPayment } from 'models/lrs';
import type { Payment } from 'models/payment';
import type { RootState } from 'reducers/types';
import { EmbeddedRepository as embedded } from 'repositories/embedded/embedded';
import { isLoggedIn } from 'selectors/entities/authentication/authentication';
import { getFieldByEntity } from 'selectors/entities/fields/fields';
import { getLinkById } from 'selectors/entities/links/links';
import {
  getOrder,
  orderHasOwner,
  paymentIsFromAgents,
} from 'selectors/entities/order/order';
import {
  getPayment,
  getPaymentId,
  getPaymentToken,
} from 'selectors/entities/payment/payment';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getCaptchaResponse } from 'selectors/ui/captcha/captcha';
import {
  getAcademicFields,
  getCurrentLocale,
  getFieldValue,
  isEmbedded,
  isFetching,
  isTrackingPage,
  shouldHideUserManagement,
  shouldReceiveWhatsappNotification,
  untilIsFetched,
} from 'selectors/ui/ui';
import { logger } from 'services/logger/logger';
import { lrs } from 'services/lrs/lrs';
import type { PaymentsService } from 'services/payex/payments/payments';
import type {
  RepeatPaymentPayload,
  UpdatePaymentRequestBody,
} from 'services/payex/payments/types';
import type { CreatePaymentPayload } from 'services/payments/payments';
import { normalizeErrors, normalizePayment } from 'services/payments/payments';
import { normalizeSmsNotifications } from 'services/smsNotifications/smsNotifications';
import type { PayexDispatch, PayexThunkAction } from 'store/configureStore';
import { callbackPayment } from 'utils/callbackPayment/callbackPayment';
import { cookies } from 'utils/cookies/cookies';
import { now } from 'utils/date/date';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

const SIGNATURE_FILE_ERROR = 'invalid_signature';
const PERFORMING_ACTION_ERROR =
  'notifications.edit_payment_details.performing_action_error';
const ENGLISH = 'en';

const getCreatePaymentCallback = ():
  | ((payment: Payment) => unknown)
  | undefined => {
  const onPaymentCreated = embedded.getProp('onPaymentCreated');

  const callback = (payment: Payment): unknown =>
    // @ts-expect-error this is coming from embedded props and it's hard to type
    onPaymentCreated(callbackPayment(payment));

  return onPaymentCreated ? callback : undefined;
};

export const createPayment =
  (platform: string): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { payments, userLanguage }) => {
    const state = getState();
    const isCreatingPayment = isFetching(state, 'createPayment');

    if (isCreatingPayment) {
      return;
    }

    dispatch({ type: CREATE_PAYMENT_REQUEST });

    const locale = getCurrentLocale(state);
    const senderEmail = getFieldValue(state, 'sender_email') as string;
    if (locale !== ENGLISH) {
      userLanguage.update(senderEmail as string, locale);
    }

    const promotionsOptIn = getFieldValue(state, PROMOTIONS_OPT_IN_FIELD);

    const { id, token } = getOrder(state);
    const captchaResponse = isEmbedded(state)
      ? CAPTCHA_VALIDATION_RESPONSE_TOKEN
      : getCaptchaResponse(state);
    const callback = getCreatePaymentCallback();
    const hideUserManagement = shouldHideUserManagement(state);

    const recipient = getRecipientId(state);
    const whatsappNotification = shouldReceiveWhatsappNotification(state);

    const payerIdentity = getFieldValue(state, PAYER_IDENTITY_FIELD) as
      | string
      | undefined;

    if (payerIdentity) {
      dispatch(trackSubmitPayerIdentity());
    }

    const params: CreatePaymentPayload = {
      orderId: id,
      token,
      locale,
      callback,
      hideUserManagement,
      captchaResponse: captchaResponse as string,
      platform,
      recipient,
      ...(whatsappNotification != null && { whatsappNotification }),
      ...(promotionsOptIn && { promotionsOptIn: true }),
    };

    try {
      const normalizedPayment = await payments.create(params);

      if (whatsappNotification !== undefined && whatsappNotification !== null) {
        dispatch(trackSubscriptionWhatsapp(whatsappNotification));
      }

      saveAcademicFieldsToPayment(state, normalizedPayment, dispatch);

      return dispatch({
        type: CREATE_PAYMENT_SUCCESS,
        payload: normalizedPayment,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status, parsedBody } = error as HttpError;

        if (status === 503) {
          return dispatch(enableFeature(MAINTENANCE_MODE));
        }

        return dispatch({
          type: CREATE_PAYMENT_FAILURE,
          payload: parsedBody,
        });
      } else {
        return dispatch({
          type: CREATE_PAYMENT_FAILURE,
        });
      }
    }
  };

export const fetchPayment =
  ({
    orderId,
    token,
  }: {
    orderId?: string;
    token?: string;
  } = {}): PayexThunkAction<Promise<{ success: boolean; payment?: Payment }>> =>
  async (dispatch, getState, { apiV3 }) => {
    const state = getState();

    await untilIsFetched(getState, 'payment');

    dispatch({ type: FETCH_PAYMENT_REQUEST });

    const locale = getCurrentLocale(state);

    if (!orderId || !token) {
      ({ id: orderId, token } = getOrder(state));
    }

    if (!orderId || !token) {
      errorNotifier.notify(
        `${FETCH_PAYMENT_FAILURE} Invalid order id (${orderId}) or token (${token}) when fetching order payment`,
      );
      dispatch({ type: FETCH_PAYMENT_FAILURE });
      return { success: false };
    }

    try {
      const response = await apiV3.orders.fetchPayment({
        orderId,
        token,
        locale,
      });

      const payment = response[response.length - 1];

      if (!payment) {
        dispatch({ type: FETCH_PAYMENT_FAILURE });
        return { success: false };
      }

      dispatch({
        type: FETCH_PAYMENT_SUCCESS,
        payload: normalizePayment(payment),
      });

      return { success: true, payment };
    } catch (_error) {
      dispatch({ type: FETCH_PAYMENT_FAILURE });
      return { success: false };
    }
  };

export const cancelPayment =
  (): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isCancellingPayment = isFetching(state, 'paymentManagement');

    if (isCancellingPayment) {
      return;
    }

    dispatch({ type: CANCEL_PAYMENT_REQUEST });

    const { href, method } = getLinkById(state, 'cancellation') || {};

    if (!href) {
      return;
    }

    let cancellationReason;
    if (paymentIsFromAgents(state)) {
      cancellationReason = 'agents';
    }

    try {
      const response = await apiV3.orders.cancelPayment({
        url: href,
        method: method as string,
        cancellationReason,
      });

      await logger.log({
        description: 'payment cancelled by user',
        parameters: {
          payment: getPayment(state),
        },
      });

      return dispatch({
        type: CANCEL_PAYMENT_SUCCESS,
        payload: response,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const httpError = error as HttpError;
        const { status } = httpError;

        if (
          status !== HTTPStatuses.NOT_FOUND &&
          status !== HTTPStatuses.BAD_REQUEST &&
          status !== HTTPStatuses.NOT_ALLOWED
        ) {
          httpError.message = `${CANCEL_PAYMENT_FAILURE} ${httpError.message}`;
          errorNotifier.notifyWithFingerprint(httpError, [
            CANCEL_PAYMENT_FAILURE,
            `${status || ''}`,
          ]);
        }
      }
      return dispatch({ type: CANCEL_PAYMENT_FAILURE });
    }
  };

export const initiatePayment =
  (): PayexThunkAction =>
  async (dispatch, getState, { apiV3 }) => {
    const state = getState();
    const isFetchingPaymentActions = isFetching(state, 'paymentActions');

    if (isFetchingPaymentActions) {
      return;
    }

    dispatch({ type: INITIATE_PAYMENT_REQUEST });

    const { href: url, method } = getLinkById(state, 'initiate') || {};
    const currentIsoDate = now();
    const response = await apiV3.orders.initiatePayment({
      url: url as string,
      method: method as string,
      date: currentIsoDate,
    });

    return dispatch({
      type: INITIATE_PAYMENT_SUCCESS,
      payload: { ...response },
    });
  };

export const fetchUserPayments =
  ({
    page = 1,
  }: {
    page?: number;
  } = {}): PayexThunkAction<Promise<{ type: string; payload: unknown }>> =>
  async (dispatch, getState, { payex }) => {
    try {
      await untilIsFetched(getState, 'payments');
      dispatch({ type: FETCH_USER_PAYMENTS_REQUEST });
      const payments = await payex.payments.fetchUserPayments({ page });

      return dispatch({
        type: FETCH_USER_PAYMENTS_SUCCESS,
        payload: payments,
      });
    } catch (error) {
      return dispatch({
        type: FETCH_USER_PAYMENTS_FAILURE,
        payload: error,
      });
    }
  };

export const fetchUserPaymentsIfNeeded =
  (): PayexThunkAction => async (dispatch, getState) => {
    const { entities: { payments = [] } = {} } = getState();

    if (payments.length > 0) {
      return;
    }

    return dispatch(fetchUserPayments());
  };

type FetchPaymentByIdReturnType =
  | Promise<undefined | ReturnType<typeof normalizePayment>>
  | Promise<Payment | undefined | ReturnType<typeof normalizePayment>>
  | undefined;

function saveAcademicFieldsToPayment(
  state: RootState,
  normalizedPayment: ReturnType<typeof normalizePayment>,
  dispatch: PayexDispatch,
) {
  try {
    const academicFields = getAcademicFields(state);
    if (academicFields) {
      const paymentId = normalizedPayment?.result;
      const payload = {
        payment_id: paymentId,
        expected_year_graduation: academicFields.expectedYearGraduation,
        program_type: academicFields.programType,
        student_personal_email: academicFields.studentPersonalEmail,
        student_mobile_number: academicFields.studentMobileNumber,
      };

      const token = normalizedPayment.entities.payments[paymentId].token;

      dispatch(saveAcademicFields({ token, payload }));
    }
  } catch (error) {
    errorNotifier.notify(error);

    return dispatch({
      type: SAVE_ACADEMIC_FIELDS_FAILURE,
    });
  }
}

export function fetchPaymentById(
  id: string,
  isUserPayment: false,
): PayexThunkAction<
  Promise<undefined | ReturnType<typeof normalizePayment>> | undefined
>;
export function fetchPaymentById(
  id: string,
  isUserPayment?: boolean,
): PayexThunkAction<
  Promise<undefined | ReturnType<typeof normalizePayment>> | undefined
>;

export function fetchPaymentById(
  id: string,
  isUserPayment?: boolean,
): PayexThunkAction<FetchPaymentByIdReturnType> {
  return async (dispatch, getState, { payex }) => {
    const state = getState();
    const isFetchingPayment = isFetching(state, 'payment');
    const locale = getCurrentLocale(state);

    if (isFetchingPayment) {
      return;
    }
    if (isUserPayment) {
      return fetchUserPaymentById(id, dispatch, { payments: payex.payments });
    }

    dispatch({ type: FETCH_PAYMENT_REQUEST });

    try {
      const payment = await payex.payments.fetchById({ id, locale });
      const { payload } = await dispatch({
        type: FETCH_PAYMENT_SUCCESS,
        payload: normalizePayment(payment),
      });
      return payload;
    } catch (_e) {
      dispatch({ type: FETCH_PAYMENT_FAILURE });
    }
  };
}

const fetchUserPaymentById = (
  paymentId: string,
  dispatch: PayexDispatch,
  { payments }: { payments: PaymentsService },
) => {
  dispatch({ type: FETCH_USER_PAYMENT_REQUEST });

  return payments
    .fetchById({
      id: paymentId,
    })
    .then(payment => {
      if (!payment) {
        return dispatch({ type: FETCH_USER_PAYMENT_FAILURE });
      }
      const payload = normalizePayment(payment);

      const {
        entities: { payments },
        result,
      } = payload;

      return dispatch({
        type: FETCH_USER_PAYMENT_SUCCESS,
        payload: payments[result],
      });
    })
    .then((response: { type: string; payload?: Payment }) => response.payload);
};

export const fetchLoggedUserPaymentById =
  (
    paymentId: string,
  ): PayexThunkAction<Promise<{ type: string; payload: unknown }>> =>
  async (dispatch, _, { payex }) => {
    dispatch({ type: FETCH_LOGGED_USER_PAYMENT_REQUEST });

    return payex.payments
      .fetchById({
        id: paymentId,
      })
      .then(payment => {
        const payload = normalizePayment(payment);
        const {
          entities: { payments },
          result,
        } = payload;

        return dispatch({
          type: FETCH_LOGGED_USER_PAYMENT_SUCCESS,
          payload: payments[result],
        });
      })
      .catch(error => {
        if (isHttpErrorWithStatus(error)) {
          const httpError = error as HttpError;
          const { status = '' } = httpError;
          if (status === HTTPStatuses.NOT_FOUND) {
            const message = PERFORMING_ACTION_ERROR;
            const noti = newNotification({ message, type: 'error' });

            dispatch(noti);
          }
        }

        dispatch({
          type: FETCH_LOGGED_USER_PAYMENT_FAILURE,
        });

        throw new Error(PAYMENT_FETCH_ERROR);
      });
  };

export const fetchSMSNotification =
  (): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { payex }) => {
    try {
      const state = getState();
      const isFetchingPaymentActions = isFetching(state, 'paymentActions');

      if (isFetchingPaymentActions) {
        return;
      }

      dispatch({ type: FETCH_SMS_NOTIFICATION_REQUEST });

      const { href: url } = getLinkById(state, 'sms_notification') || {};
      const response = await payex.payments.fetchSMSNotification({
        url: url as string,
      });

      return dispatch({
        type: FETCH_SMS_NOTIFICATION_SUCCESS,
        payload: normalizeSmsNotifications(response),
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const httpError = error as HttpError;
        const { status = '' } = httpError;
        if (status !== HTTPStatuses.NOT_FOUND) {
          httpError.message = `${FETCH_SMS_NOTIFICATION_FAILURE} ${httpError.message}`;
          errorNotifier.notifyWithFingerprint(httpError, [
            FETCH_SMS_NOTIFICATION_FAILURE,
            `${status}`,
          ]);
        }
      }
      return dispatch({ type: FETCH_SMS_NOTIFICATION_FAILURE });
    }
  };

export const updatePayment =
  (): PayexThunkAction<Promise<void>> =>
  (dispatch, getState, { payex }) => {
    const state = getState();
    const isUpdatingPayment = isFetching(state, 'payment');

    if (isUpdatingPayment) {
      return Promise.reject(new Error('Already updating a payment...'));
    }

    dispatch({ type: UPDATE_PAYMENT_REQUEST });

    const token = getPaymentToken(state);
    const id = getPaymentId(state);
    const payload = updatePaymentPayload(state);

    return payex.payments
      .update({ ...payload, id, token } as UpdatePaymentRequestBody)
      .then(payment => {
        updateSenderEmail(dispatch, payload);

        dispatch({
          type: UPDATE_PAYMENT_SUCCESS,
          payload: {
            sender: payment.sender,
            offer: payment.offer,
            recipient: payment.recipient,
          },
        });
      })
      .catch(error => {
        const {
          parsedBody: { errors },
        } = error;

        dispatch({
          type: UPDATE_PAYMENT_FAILURE,
          payload: normalizeErrors(errors),
        });

        throw new Error();
      });
  };

const updateSenderEmail = (
  dispatch: PayexDispatch,
  payload: ReturnType<typeof updatePaymentPayload>,
) => {
  const {
    sender: { fields },
  } = payload;
  const email = fields.find(({ id }) => id === 'email')?.value;

  dispatch({ type: UPDATE_SENDER_EMAIL, payload: email });
};

const updatePaymentPayload = (state: RootState) => {
  const shouldNotFilterEncryptedFields = () =>
    !isTrackingPage(state) || (orderHasOwner(state) && isLoggedIn(state));

  const filterChineseId = (fieldId: string) => {
    if (shouldNotFilterEncryptedFields()) {
      return true;
    }
    const chineseFields = [
      `offer_${CHINESE_FIELD_ID}`,
      `offer_${STUDENT_CHINESE_FIELD_ID}`,
    ];
    return !chineseFields.includes(fieldId);
  };

  const filterStudentCnyIdNumber = (fieldId: string) => {
    if (shouldNotFilterEncryptedFields()) {
      return true;
    }
    return fieldId !== STUDENT_CNY_ID_NUMBER;
  };

  const filterStudentPassport = (fieldId: string) => {
    if (shouldNotFilterEncryptedFields()) {
      return true;
    }
    return fieldId !== STUDENT_PASSPORT_FIELD_ID;
  };

  const filterPayerPassport = (fieldId: string) => {
    if (shouldNotFilterEncryptedFields()) {
      return true;
    }
    return fieldId !== PAYER_PASSPORT_FIELD_ID;
  };

  const entityFieldsPayload = (name: FieldEntityType) => {
    const entity = getFieldByEntity(state, name);
    const { byId: fields, ids: fieldList } = entity;

    return fieldList
      .filter(filterChineseId)
      .filter(filterStudentCnyIdNumber)
      .filter(filterStudentPassport)
      .filter(filterPayerPassport)
      .filter((fieldId: string) => {
        const field = fields[fieldId];
        return 'updatableForPayments' in field
          ? field.updatableForPayments
          : true;
      })
      .map(field => {
        const { name } = fields[field];
        const value = getFieldValue(state, field);
        return { id: name, value };
      });
  };

  return {
    recipient: {
      fields: entityFieldsPayload('recipient'),
    },
    offer: {
      fields: entityFieldsPayload('offers'),
    },
    sender: {
      fields: entityFieldsPayload('sender'),
    },
  };
};

export const deletePaymentLRS =
  (): PayexThunkAction<Promise<void>> =>
  async (dispatch, getState, { lrs }) => {
    const state = getState();
    const isFetchingPayment = isFetching(state, 'payment');

    if (isFetchingPayment) {
      return;
    }

    const paymentId = getPaymentId(state);
    const paymentToken = getPaymentToken(state);

    dispatch({ type: DELETE_PAYMENT_LRS_REQUEST });
    try {
      await lrs.delete({ paymentId, paymentToken });
      dispatch({ type: DELETE_PAYMENT_LRS_SUCCESS });
      dispatch(setDocCollectorFinished(false));
      dispatch(fetchDocuments());
    } catch (_error) {
      dispatch({ type: DELETE_PAYMENT_LRS_FAILURE });
    }
  };

type UploadLRSParams = {
  bankAccountNumber: string;
  studentFullName: string;
  studentID: string;
  transactions: PreviousPayment[];
  signatureImage: string;
};

export const uploadLRS =
  (data: UploadLRSParams): PayexThunkAction<Promise<{ success: boolean }>> =>
  async (dispatch, getState) => {
    const state = getState();
    const paymentId = getPaymentId(state);
    const paymentToken = getPaymentToken(state);

    dispatch({ type: UPLOAD_LRS_REQUEST });
    try {
      await lrs.upload({
        ...data,
        paymentId,
        paymentToken,
      });
      dispatch(setDocCollectorFinished(true));
      dispatch(fetchDocuments());
      dispatch({ type: UPLOAD_LRS_SUCCESS });
      return { success: true };
    } catch (error) {
      let message = `notifications.${UPLOAD_LRS_FAILURE.toLowerCase()}`;
      if (isHttpErrorWithParsedBody(error)) {
        const httpError = error as HttpError;
        httpError.message = `${UPLOAD_LRS_FAILURE} ${httpError.message}`;
        errorNotifier.notifyWithFingerprint(
          httpError,
          [UPLOAD_LRS_FAILURE, `${httpError.status || ''}`],
          { ...data, paymentId },
        );

        const { parsedBody } = httpError;
        if (
          parsedBody?.error &&
          typeof parsedBody?.error === 'string' &&
          SIGNATURE_FILE_ERROR === parsedBody.error.toLowerCase()
        ) {
          message = `${message}.${SIGNATURE_FILE_ERROR}`;
          dispatch({ type: UPLOAD_LRS_SIGNATURE_FILE_BAD_FORMAT });
        }
      }
      const noti = newNotification({ message, type: 'error' });

      dispatch(noti);

      dispatch({ type: UPLOAD_LRS_FAILURE });

      return { success: false };
    }
  };

type SaveLRSFormDataParams = {
  bankAccountNumber: string;
  studentFullName: string;
  studentID: string;
  transactions: PreviousPayment[];
};

export const saveLRSFormData =
  (
    data: SaveLRSFormDataParams,
  ): PayexThunkAction<Promise<{ success: boolean }>> =>
  async (dispatch, getState) => {
    const state = getState();
    const paymentId = getPaymentId(state);
    const paymentToken = getPaymentToken(state);

    dispatch({ type: SAVE_LRS_FORM_DATA_REQUEST });
    try {
      await lrs.saveFormData({
        ...data,
        paymentId,
        paymentToken,
      });
      dispatch({ type: SAVE_LRS_FORM_DATA_SUCCESS });
      return { success: true };
    } catch (error) {
      errorNotifier.notify(error, { ...data, paymentId });
      dispatch(
        newNotification({
          message: 'notifications.upload_lrs_failure',
          type: 'error',
        }),
      );
      return { success: false };
    }
  };

export const previewLRSData =
  (
    data: object,
  ): PayexThunkAction<
    Promise<{ success: false } | { success: true; data: ArrayBuffer }>
  > =>
  async (dispatch, getState) => {
    const state = getState();
    const paymentId = getPaymentId(state);
    const paymentToken = getPaymentToken(state);

    try {
      const lrsData = await lrs.previewForm({
        ...data,
        paymentId,
        paymentToken,
      });

      return { success: true, data: lrsData };
    } catch (error) {
      errorNotifier.notify(error, {
        ...data,
        paymentId,
      });
      dispatch({ type: UPLOAD_LRS_FAILURE });
      return { success: false };
    }
  };

export const repeatPaymentOrder =
  (): PayexThunkAction<Promise<Payment>> =>
  async (dispatch, getState, { payex }) => {
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || 'no_fingerprint';
    const state = getState();
    const payment = getPayment(state);
    const locale = getCurrentLocale(state);

    const dynamicFields = entitiesPayload(['offers', 'recipient'], state);

    if (!dynamicFields.recipient.items) {
      throw new Error('Items are required for repeat payment');
    }

    const payload: RepeatPaymentPayload = {
      id: payment.id,
      paymentToken: payment.token,
      items: dynamicFields.recipient.items,
      fingerprint,
      locale,
      offerFields: dynamicFields.offer?.fields,
    };
    const repeatedOrder = await payex.payments.repeatPaymentOrder(payload);
    dispatch({ type: CREATE_ORDER_SUCCESS, payload: repeatedOrder });

    return repeatedOrder;
  };

export const changePaymentMethod =
  (): PayexThunkAction<Promise<{ type: string; payload: unknown }>> =>
  async (dispatch, getState, { payex }) => {
    const state = getState();
    const currentPayment = getPayment(state);
    const { id, token } = getOrder(state);
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || 'no_fingerprint';
    const locale = getCurrentLocale(state);
    const payload = {
      orderId: id,
      orderToken: token,
      paymentId: currentPayment.id,
      paymentToken: currentPayment.token,
      fingerprint,
      locale,
    };
    const payment = await payex.payments.changePaymentMethod(payload);
    return dispatch({
      type: CREATE_PAYMENT_SUCCESS,
      payload: normalizePayment(payment),
    });
  };

export const completePaymentProof = () => ({
  type: PAYMENT_PROOF_COMPLETED,
});

export const fetchDocumentsCollections =
  (): PayexThunkAction =>
  async (dispatch, getState, { payex }) => {
    const state = getState();
    const isFetchingDocumentsCollections = isFetching(
      state,
      'documentsCollections',
    );
    const paymentId = getPaymentId(state);

    if (!paymentId || isFetchingDocumentsCollections) {
      return;
    }

    dispatch({ type: FETCH_DOCUMENTS_COLLECTIONS_REQUEST });

    const { token } = getPayment(state);
    const locale = getCurrentLocale(state);
    try {
      const response = await payex.payments.fetchDocumentsCollections({
        id: paymentId,
        token,
        locale,
      });

      return dispatch({
        type: FETCH_DOCUMENTS_COLLECTIONS_SUCCESS,
        payload: response,
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status } = error as HttpError;

        if (status === HTTPStatuses.NOT_FOUND) {
          return dispatch({
            type: FETCH_DOCUMENTS_COLLECTIONS_SUCCESS_WITHOUT_RESULTS,
          });
        }
      }

      errorNotifier.notify(error);

      return dispatch({ type: FETCH_DOCUMENTS_COLLECTIONS_FAILURE });
    }
  };

export const setCreatePaymentFailure = (errors: ApiError[]) => ({
  type: CREATE_PAYMENT_FAILURE,
  payload: { errors },
});
